import type { ReactNode } from 'react';
import { DRESS_SUGGESTIONS, MEAL_LABELS, PHASE_LABELS, TIPS_LABELS, type BriefingDraft, type Meal, type Phase, type Tips } from '../briefing.ts';

// Points de départ classiques : le restaurateur ajuste ensuite.
const CHECKLIST_SUGGESTIONS: { phase: Phase; label: string; required: boolean }[] = [
  { phase: 'BEFORE', label: 'Arriver 15 minutes avant le début', required: true },
  { phase: 'BEFORE', label: 'Se présenter au responsable et récupérer la tenue', required: false },
  { phase: 'DURING', label: 'Participer au briefing d’équipe', required: false },
  { phase: 'DURING', label: 'Dresser et vérifier les tables', required: false },
  { phase: 'AFTER', label: 'Débarrasser et nettoyer son poste', required: true },
  { phase: 'AFTER', label: 'Faire valider ses heures avec le responsable', required: true },
];

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontWeight: 800 }}>{title}</div>
        {hint && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5 }}>{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, id, children }: { label: string; id: string; children: ReactNode }) {
  return (
    <div>
      <label className="field-label" htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

/**
 * Fiche pratique d'une mission, vue de l'extra : tenue, repas, pauses, planning, check-list, accès et contacts.
 */
export function BriefingForm({ value, onChange }: { value: BriefingDraft; onChange: (v: BriefingDraft) => void }) {
  const set = <K extends keyof BriefingDraft>(key: K, v: BriefingDraft[K]) => onChange({ ...value, [key]: v });

  return (
    <>
      <Section title="Tenue, matériel et accès" hint="Ce que l'extra doit savoir avant de candidater. Visible de tous les extras qui consultent la mission.">
        <Field label="Tenue demandée" id="dress">
          <input id="dress" className="input" maxLength={400} value={value.dressCode} onChange={(e) => set('dressCode', e.target.value)} placeholder="Chemise blanche, pantalon noir, tablier fourni" />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {DRESS_SUGGESTIONS.map((s) => (
              <button key={s} type="button" className="btn btn-ghost btn-sm" onClick={() => set('dressCode', value.dressCode ? `${value.dressCode}, ${s.toLowerCase()}` : s)}>+ {s}</button>
            ))}
          </div>
        </Field>
        <Field label="Matériel à apporter ou fourni" id="equipment">
          <input id="equipment" className="input" maxLength={500} value={value.equipmentNotes} onChange={(e) => set('equipmentNotes', e.target.value)} placeholder="Tire-bouchon et stylo personnels ; plateaux fournis" />
        </Field>
        <Field label="Parking et transports" id="parking">
          <input id="parking" className="input" maxLength={300} value={value.parkingInfo} onChange={(e) => set('parkingInfo', e.target.value)} placeholder="Parking à 5 min à pied, métro ligne A" />
        </Field>
        <Field label="Informations diverses" id="notes">
          <textarea id="notes" className="input" rows={3} maxLength={2000} value={value.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Nombre de couverts, ambiance, public, consignes particulières…" />
        </Field>
      </Section>

      <Section title="Repas, pauses et pourboires">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Repas" id="meal">
            <select id="meal" className="input" value={value.meal} onChange={(e) => set('meal', e.target.value as Meal)}>
              {(Object.keys(MEAL_LABELS) as Meal[]).map((m) => <option key={m} value={m}>{MEAL_LABELS[m]}</option>)}
            </select>
          </Field>
          <Field label="Pourboires" id="tips">
            <select id="tips" className="input" value={value.tips} onChange={(e) => set('tips', e.target.value as Tips)}>
              {(Object.keys(TIPS_LABELS) as Tips[]).map((t) => <option key={t} value={t}>{TIPS_LABELS[t]}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'end' }}>
          <Field label="Pause (minutes)" id="break">
            <input id="break" className="input" type="number" min={0} max={240} step={5} value={value.breakMinutes} onChange={(e) => set('breakMinutes', e.target.value)} placeholder="0" />
          </Field>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, paddingBottom: 10 }}>
            <input type="checkbox" checked={value.breakPaid} onChange={(e) => set('breakPaid', e.target.checked)} /> Pause rémunérée
          </label>
        </div>
      </Section>

      <Section title="Brief et planning de la prestation" hint="Les horaires suivent le début de la mission : décaler la mission décale le planning.">
        <Field label="Brief avant le service (minutes avant le début)" id="briefing">
          <input id="briefing" className="input" type="number" min={0} max={720} step={5} value={value.briefingMinutesBefore} onChange={(e) => set('briefingMinutesBefore', e.target.value)} placeholder="Ex. 30" />
        </Field>
        {value.schedule.map((s, i) => (
          <div key={s.id ?? i} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 70px auto', gap: 6, alignItems: 'center' }}>
            <input className="input" type="time" aria-label={`Heure de l’étape ${i + 1}`} value={s.time} onChange={(e) => set('schedule', value.schedule.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))} />
            <input className="input" aria-label={`Étape ${i + 1}`} maxLength={120} value={s.label} placeholder="Mise en place, service du plat…" onChange={(e) => set('schedule', value.schedule.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            <input className="input" type="number" min={1} aria-label={`Durée de l’étape ${i + 1} (minutes)`} value={s.durationMin} placeholder="min" onChange={(e) => set('schedule', value.schedule.map((x, j) => (j === i ? { ...x, durationMin: e.target.value } : x)))} />
            <button type="button" className="btn btn-ghost btn-sm" aria-label={`Retirer l’étape ${i + 1}`} onClick={() => set('schedule', value.schedule.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={value.schedule.length >= 30}
          onClick={() => set('schedule', [...value.schedule, { time: value.schedule.at(-1)?.time ?? '', durationMin: '', label: '' }])}>
          + Ajouter une étape
        </button>
      </Section>

      <Section title="Check-list de la mission" hint="L'extra la coche sur place ; vous suivez l'avancement. Les points « obligatoires » sont signalés.">
        {value.checklist.map((c, i) => (
          <div key={c.id ?? i} style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', gap: 6, alignItems: 'center' }}>
            <select className="input" aria-label={`Moment du point ${i + 1}`} value={c.phase} onChange={(e) => set('checklist', value.checklist.map((x, j) => (j === i ? { ...x, phase: e.target.value as Phase } : x)))}>
              {(Object.keys(PHASE_LABELS) as Phase[]).map((p) => <option key={p} value={p}>{PHASE_LABELS[p].replace(' le service', '')}</option>)}
            </select>
            <input className="input" aria-label={`Point ${i + 1}`} maxLength={160} value={c.label} placeholder="Ex. Dresser les tables" onChange={(e) => set('checklist', value.checklist.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="checkbox" checked={c.required} onChange={(e) => set('checklist', value.checklist.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} /> Oblig.
              </label>
              <button type="button" className="btn btn-ghost btn-sm" aria-label={`Retirer le point ${i + 1}`} onClick={() => set('checklist', value.checklist.filter((_, j) => j !== i))}>✕</button>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary btn-sm" disabled={value.checklist.length >= 40} onClick={() => set('checklist', [...value.checklist, { phase: 'DURING', label: '', required: false }])}>+ Ajouter un point</button>
          {value.checklist.length === 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => set('checklist', CHECKLIST_SUGGESTIONS)}>Partir d’une check-list type</button>
          )}
        </div>
      </Section>

      <Section title="Accès et contacts" hint="Confidentiel : visible uniquement de votre équipe et de l'extra une fois la mission confirmée, jusqu'à la fin du service.">
        <Field label="Consignes d’arrivée" id="arrival">
          <textarea id="arrival" className="input" rows={2} maxLength={600} value={value.arrivalInstructions} onChange={(e) => set('arrivalInstructions', e.target.value)} placeholder="Entrée par la cour, demander Paul à l’office" />
        </Field>
        <Field label="Code d’accès ou digicode" id="code">
          <input id="code" className="input" maxLength={60} value={value.accessCode} onChange={(e) => set('accessCode', e.target.value)} placeholder="B4521" autoComplete="off" />
        </Field>
        <Field label="Personne à demander sur place" id="contact">
          <input id="contact" className="input" maxLength={100} value={value.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Paul Durand" />
        </Field>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Aucun numéro de téléphone : extra et établissement échangent uniquement par la messagerie de l'application, y compris le jour J (messages urgents et réponses rapides).
        </div>
      </Section>
    </>
  );
}
